import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import {
  limitOrderAPI,
  scheduledOrderAPI,
  marketsAPI,
  getErrorMessage,
} from '../../utils/api';
import {
  formatCurrency,
  formatDate,
  getStatusBadgeClass,
  formatStatusText,
} from '../../utils/helpers';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Trash2, RefreshCw, ArrowDown, ArrowUp } from 'lucide-react';

const Orders = () => {
  const [tab, setTab] = useState('limit');
  const [limitOrders, setLimitOrders] = useState([]);
  const [scheduledOrders, setScheduledOrders] = useState([]);
  const [prices, setPrices] = useState({}); // symbol -> current_price
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null); // {kind, id}

  const loadAll = useCallback(async () => {
    try {
      const [limitRes, schedRes, marketsRes] = await Promise.all([
        limitOrderAPI.getAll(),
        scheduledOrderAPI.getAll(),
        marketsAPI.getCryptocurrencies().catch(() => ({ data: [] })),
      ]);
      setLimitOrders(Array.isArray(limitRes.data) ? limitRes.data : []);
      setScheduledOrders(Array.isArray(schedRes.data) ? schedRes.data : []);
      const priceMap = {};
      (marketsRes.data || []).forEach((c) => {
        if (c.symbol) priceMap[c.symbol.toUpperCase()] = c.current_price;
      });
      setPrices(priceMap);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load orders'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    const { kind, id } = cancelTarget;
    setCancelTarget(null);
    try {
      if (kind === 'limit') {
        await limitOrderAPI.cancel(id);
        setLimitOrders((prev) =>
          prev.map((o) => (o.id === id ? { ...o, status: 'cancelled' } : o))
        );
      } else {
        await scheduledOrderAPI.cancel(id);
        setScheduledOrders((prev) => prev.filter((o) => o.id !== id));
      }
      toast.success('Order cancelled');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to cancel order'));
    }
  };

  const limitRows = useMemo(
    () =>
      [...limitOrders].sort(
        (a, b) =>
          new Date(b.created_at || 0).getTime() -
          new Date(a.created_at || 0).getTime()
      ),
    [limitOrders]
  );

  const schedRows = useMemo(
    () =>
      [...scheduledOrders].sort(
        (a, b) =>
          new Date(a.next_execution || 0).getTime() -
          new Date(b.next_execution || 0).getTime()
      ),
    [scheduledOrders]
  );

  if (loading)
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loading-spinner"></div>
      </div>
    );

  return (
    <div className="space-y-8 animate-fadeIn" data-testid="orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-black mb-2">Orders</h1>
          <p className="text-gray-600">Manage your limit and scheduled orders</p>
        </div>
        <Button onClick={loadAll} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="limit">Limit Orders ({limitRows.length})</TabsTrigger>
          <TabsTrigger value="scheduled">
            Scheduled Orders ({schedRows.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="limit" className="mt-4">
          <Card className="glass-card p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Symbol</TableHead>
                    <TableHead>Side</TableHead>
                    <TableHead className="text-right">Target Price</TableHead>
                    <TableHead className="text-right">Current Price</TableHead>
                    <TableHead className="text-right">% Distance</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {limitRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                        No limit orders yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    limitRows.map((o) => {
                      const symbol = (o.asset_symbol || '').toUpperCase();
                      const current =
                        o.current_price ?? prices[symbol] ?? null;
                      const target = Number(o.target_price);
                      const distancePct =
                        current != null && target
                          ? ((current - target) / target) * 100
                          : null;
                      const side = (o.trade_type || '').toLowerCase();
                      const cancellable = o.status === 'active';
                      return (
                        <TableRow key={o.id}>
                          <TableCell className="font-medium">{symbol}</TableCell>
                          <TableCell>
                            <span
                              className={`inline-flex items-center text-xs font-semibold uppercase ${
                                side === 'buy' ? 'text-green-600' : 'text-red-600'
                              }`}
                            >
                              {side === 'buy' ? (
                                <ArrowDown className="w-3 h-3 mr-1" />
                              ) : (
                                <ArrowUp className="w-3 h-3 mr-1" />
                              )}
                              {side || '—'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(target)}
                          </TableCell>
                          <TableCell className="text-right">
                            {current != null ? formatCurrency(current) : '—'}
                          </TableCell>
                          <TableCell
                            className={`text-right ${
                              distancePct == null
                                ? ''
                                : distancePct >= 0
                                ? 'text-green-600'
                                : 'text-red-600'
                            }`}
                          >
                            {distancePct == null
                              ? '—'
                              : `${distancePct >= 0 ? '+' : ''}${distancePct.toFixed(2)}%`}
                          </TableCell>
                          <TableCell className="text-right">{o.amount}</TableCell>
                          <TableCell>
                            <span className={`badge ${getStatusBadgeClass(o.status)}`}>
                              {formatStatusText(o.status)}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs text-gray-500">
                            {formatDate(o.created_at)}
                          </TableCell>
                          <TableCell>
                            {cancellable && (
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() =>
                                  setCancelTarget({ kind: 'limit', id: o.id })
                                }
                                aria-label="Cancel order"
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="scheduled" className="mt-4">
          <Card className="glass-card p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Symbol</TableHead>
                    <TableHead>Side</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Frequency</TableHead>
                    <TableHead>Next Execution</TableHead>
                    <TableHead>Last Execution</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {schedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                        No scheduled orders yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    schedRows.map((o) => {
                      const symbol = (o.asset_symbol || '').toUpperCase();
                      const side = (o.trade_type || '').toLowerCase();
                      const status = o.is_active === false ? 'paused' : 'active';
                      return (
                        <TableRow key={o.id}>
                          <TableCell className="font-medium">{symbol}</TableCell>
                          <TableCell>
                            <span
                              className={`text-xs font-semibold uppercase ${
                                side === 'buy' ? 'text-green-600' : 'text-red-600'
                              }`}
                            >
                              {side || '—'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">{o.amount}</TableCell>
                          <TableCell className="capitalize">
                            {o.frequency || '—'}
                          </TableCell>
                          <TableCell className="text-xs text-gray-500">
                            {o.next_execution ? formatDate(o.next_execution) : '—'}
                          </TableCell>
                          <TableCell className="text-xs text-gray-500">
                            {o.last_execution ? formatDate(o.last_execution) : '—'}
                          </TableCell>
                          <TableCell>
                            <span className={`badge ${getStatusBadgeClass(status)}`}>
                              {formatStatusText(status)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() =>
                                setCancelTarget({ kind: 'scheduled', id: o.id })
                              }
                              aria-label="Cancel scheduled order"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <AlertDialog
        open={cancelTarget !== null}
        onOpenChange={(open) => {
          if (!open) setCancelTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel order?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. The order will be cancelled and any reserved
              balance released.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep order</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancel}>
              Cancel order
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Orders;
